import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import '../styles/CartCalculator.css';

const CartCalculator = () => {
  const { user } = useAuth();
  
  // Data states
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [settings, setSettings] = useState({ laborCostPerHour: 150 });
  const [loading, setLoading] = useState(true);

  // Selection states
  const [selectedProducts, setSelectedProducts] = useState({});
  const [selectedParts, setSelectedParts] = useState({});
  const [selectedPriceList, setSelectedPriceList] = useState(null);
  const [cartQuantity, setCartQuantity] = useState(1);
  
  // Sale & Rental states
  const [isSale, setIsSale] = useState(true);
  const [isRental, setIsRental] = useState(false);
  const [rentalYears, setRentalYears] = useState(5);
  const [interestRate, setInterestRate] = useState(7);

  // Product BOMs cache
  const [productBoms, setProductBoms] = useState({});
  
  // Drag state
  const [draggedCategory, setDraggedCategory] = useState(null);
  const [localCategoryOrder, setLocalCategoryOrder] = useState([]);

  // Collapse state
  const [collapsedCategories, setCollapsedCategories] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (categories.length > 0 && localCategoryOrder.length === 0) {
      setLocalCategoryOrder(categories.map(c => c._id));
    }
  }, [categories]);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes, priceListsRes, settingsRes] = await Promise.all([
        api.get('/products'),
        api.get('/categories'),
        api.get('/price-lists'),
        api.get('/settings')
      ]);
      
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setPriceLists(priceListsRes.data);
      setSettings(settingsRes.data);
      
      if (priceListsRes.data.length > 0) {
        const minLevel = user?.minPriceList || 1;
        const defaultPriceList = priceListsRes.data.find(p => p.level >= minLevel) || priceListsRes.data[0];
        setSelectedPriceList(defaultPriceList);
      }

      for (const product of productsRes.data) {
        fetchProductBom(product._id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductBom = async (productId) => {
    if (productBoms[productId]) return productBoms[productId];
    
    try {
      const response = await api.get(`/boms/${productId}`);
      const bomData = response.data;
      setProductBoms(prev => ({ ...prev, [productId]: bomData }));
      return bomData;
    } catch (error) {
      console.error('Error fetching BOM:', error);
      return [];
    }
  };

  const handleDropdownSelect = (categoryName, productId) => {
    const newSelected = { ...selectedProducts };
    
    Object.keys(newSelected).forEach(key => {
      const prod = products.find(p => p._id === key);
      if (prod?.category === categoryName) {
        delete newSelected[key];
      }
    });
    
    if (productId) {
      const product = products.find(p => p._id === productId);
      newSelected[productId] = { product, quantity: 1 };
    }
    
    setSelectedProducts(newSelected);
  };

  const handlePartToggle = (bomItem, categoryName) => {
    const part = bomItem.part;
    if (!part) return;
    
    const partId = part._id;
    
    setSelectedParts(prev => {
      if (prev[partId]) {
        const newSelected = { ...prev };
        delete newSelected[partId];
        return newSelected;
      } else {
        return {
          ...prev,
          [partId]: { part: part, quantity: 1, category: categoryName }
        };
      }
    });
  };

  const handlePartQuantityChange = (partId, quantity) => {
    setSelectedParts(prev => ({
      ...prev,
      [partId]: { ...prev[partId], quantity }
    }));
  };

  const calculateProductCost = (productId) => {
    const bom = productBoms[productId] || [];
    const product = products.find(p => p._id === productId);
    
    const partsCost = bom.reduce((total, item) => {
      return total + (item.part?.cost || 0) * item.quantity;
    }, 0);
    
    const laborCost = (product?.laborHours || 0) * (settings.laborCostPerHour || 150);
    
    return partsCost + laborCost;
  };

  const calculateTotalCost = () => {
    let total = 0;
    
    Object.entries(selectedProducts).forEach(([productId, { quantity }]) => {
      total += calculateProductCost(productId) * quantity;
    });
    
    Object.entries(selectedParts).forEach(([partId, { part, quantity }]) => {
      total += (part?.cost || 0) * quantity;
    });
    
    return total * cartQuantity;
  };

  const calculateSellingPrice = () => {
    const cost = calculateTotalCost();
    const margin = selectedPriceList?.marginPercent || 0;
    return cost * (1 + margin / 100);
  };

  const calculateProfit = () => {
    return calculateSellingPrice() - calculateTotalCost();
  };

  const calculateProfitPercent = () => {
    const cost = calculateTotalCost();
    if (cost === 0) return 0;
    return (calculateProfit() / cost) * 100;
  };

  // PMT formula: r*P / (1 - (1+r)^-n)
  const calculateMonthlyRental = () => {
    const P = calculateSellingPrice(); // Principal amount
    const annualRate = interestRate / 100; // Convert to decimal
    const r = annualRate / 12; // Monthly interest rate
    const n = rentalYears * 12; // Total number of payments
    
    if (r === 0) return P / n; // No interest case
    
    return (r * P) / (1 - Math.pow(1 + r, -n));
  };

  const calculateTotalRental = () => {
    return calculateMonthlyRental() * rentalYears * 12;
  };

  const calculateRentalInterest = () => {
    return calculateTotalRental() - calculateSellingPrice();
  };

  const handleReset = () => {
    setSelectedProducts({});
    setSelectedParts({});
    setCartQuantity(1);
    setIsSale(true);
    setIsRental(false);
    setRentalYears(5);
    setInterestRate(7);
  };

  // Drag handlers
  const handleDragStart = (e, categoryId) => {
    setDraggedCategory(categoryId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, categoryId) => {
    e.preventDefault();
    if (draggedCategory === categoryId) return;
    
    const newOrder = [...localCategoryOrder];
    const draggedIndex = newOrder.indexOf(draggedCategory);
    const targetIndex = newOrder.indexOf(categoryId);
    
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedCategory);
    
    setLocalCategoryOrder(newOrder);
  };

  const handleDragEnd = async () => {
    setDraggedCategory(null);
    
    const orders = localCategoryOrder.map((id, index) => ({ id, order: index }));
    try {
      await api.put('/categories/reorder/all', { orders });
    } catch (error) {
      console.error('Error saving order:', error);
    }
  };

const toggleCategoryCollapse = (categoryId) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const getSortedCategories = () => {
    if (localCategoryOrder.length === 0) return categories;
    return localCategoryOrder
      .map(id => categories.find(c => c._id === id))
      .filter(Boolean);
  };

  const getProductsByCategory = (categoryName) => {
    return products.filter(p => p.category === categoryName);
  };

  const getBomItemsForCategory = (categoryName) => {
    const categoryProducts = getProductsByCategory(categoryName);
    if (categoryProducts.length === 0) return [];
    
    const product = categoryProducts[0];
    return productBoms[product._id] || [];
  };

  const getSelectedForDropdown = (categoryName) => {
    const categoryProducts = getProductsByCategory(categoryName);
    for (const prod of categoryProducts) {
      if (selectedProducts[prod._id]) {
        return prod._id;
      }
    }
    return '';
  };

  if (loading) {
    return <div className="loading">טוען...</div>;
  }

  return (
    <div className="cart-calculator">
      <div className="page-header">
        <h1>מחשבון מחיר עגלה</h1>
        <button className="btn-reset" onClick={handleReset}>איפוס</button>
      </div>

      <div className="calculator-layout">
        <div className="products-section">
          {getSortedCategories().map(category => {
            const categoryProducts = getProductsByCategory(category.name);
            
            if (category.type === 'dropdown') {
              if (categoryProducts.length === 0) return null;
              
              return (
                <div 
                  key={category._id} 
                  className={`category-box ${draggedCategory === category._id ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, category._id)}
                  onDragOver={(e) => handleDragOver(e, category._id)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="category-header" onClick={() => toggleCategoryCollapse(category._id)}>
                    <h3>{category.name}</h3>
                    <div className="header-actions">
                      {category.isRequired && <span className="required-badge">חובה</span>}
                      <span className={`collapse-icon ${collapsedCategories[category._id] ? 'collapsed' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </div>
                  
                  <div className={`category-content ${collapsedCategories[category._id] ? 'collapsed' : ''}`}>
                    <div className="dropdown-select">
                    <select
                      value={getSelectedForDropdown(category.name)}
                      onChange={(e) => handleDropdownSelect(category.name, e.target.value)}
                    >
                      <option value="">-- בחר {category.name} --</option>
                      {categoryProducts.map(product => (
                        <option key={product._id} value={product._id}>
                          {product.name}
                          {user?.role !== 'sales' && ` (₪${calculateProductCost(product._id).toFixed(0)})`}
                        </option>
                      ))}
                    </select>
                    </div>
                  </div>
                </div>
              );
            } else {
              const bomItems = getBomItemsForCategory(category.name);
              
              if (bomItems.length === 0) return null;
              
              return (
                <div 
                  key={category._id} 
                  className={`category-box ${draggedCategory === category._id ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, category._id)}
                  onDragOver={(e) => handleDragOver(e, category._id)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="category-header" onClick={() => toggleCategoryCollapse(category._id)}>
                    <h3>{category.name}</h3>
                    <div className="header-actions">
                      {category.isRequired && <span className="required-badge">חובה</span>}
                      <span className={`collapse-icon ${collapsedCategories[category._id] ? 'collapsed' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </div>
                  
                  <div className={`category-content ${collapsedCategories[category._id] ? 'collapsed' : ''}`}>
                    <div className="checkbox-list">
                    {bomItems.map(bomItem => {
                      const part = bomItem.part;
                      if (!part) return null;
                      
                      const isSelected = !!selectedParts[part._id];
                      const partData = selectedParts[part._id];
                      
                      return (
                        <div 
                          key={part._id} 
                          className={`product-item ${isSelected ? 'selected' : ''}`}
                        >
                          <label className="product-checkbox">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handlePartToggle(bomItem, category.name)}
                            />
                            <span className="product-name">{part.nameHe}</span>
                          </label>
                          
                          {isSelected && (
                            <input
                              type="number"
                              min="1"
                              value={partData?.quantity || 1}
                              onChange={(e) => handlePartQuantityChange(part._id, parseInt(e.target.value) || 1)}
                              className="quantity-input"
                            />
                          )}
                          
                          {user?.role !== 'sales' && (
                            <span className="product-cost">
                              ₪{part.cost?.toFixed(0) || 0}
                            </span>
                          )}
                        </div>
                      );
                    })}
                    </div>
                  </div>
                </div>
              );
            }
          })}
        </div>

        <div className="settings-section">
          <div className="setting-card">
            <label>מחירון:</label>
            <select
              value={selectedPriceList?._id || ''}
              onChange={(e) => {
                const pl = priceLists.find(p => p._id === e.target.value);
                setSelectedPriceList(pl);
              }}
            >
              {priceLists.map(pl => (
                <option key={pl._id} value={pl._id}>
                  {pl.name}{user?.role !== 'sales' ? ` (${pl.marginPercent}%)` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="setting-card">
            <label>כמות עגלות:</label>
            <input
              type="number"
              min="1"
              value={cartQuantity}
              onChange={(e) => setCartQuantity(parseInt(e.target.value) || 1)}
            />
          </div>

{/* Sale & Rental Options */}
          <div className="setting-card pricing-options-card">
            <div className="pricing-checkboxes">
              <label className="pricing-checkbox">
                <input
                  type="checkbox"
                  checked={isSale}
                  onChange={(e) => setIsSale(e.target.checked)}
                />
                מכירה
              </label>
              
              <label className="pricing-checkbox">
                <input
                  type="checkbox"
                  checked={isRental}
                  onChange={(e) => setIsRental(e.target.checked)}
                />
                השכרה
              </label>
            </div>
            
            {isRental && (
              <div className="rental-options">
                <div className="rental-field">
                  <label>שנים:</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={rentalYears}
                    onChange={(e) => setRentalYears(parseInt(e.target.value) || 1)}
                  />
                </div>
                <div className="rental-field">
                  <label>ריבית (%):</label>
                  <input
                    type="number"
                    min="0"
                    step="0.125"
                    value={interestRate}
                    onChange={(e) => setInterestRate(parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
            )}
          </div>

{/* Results */}
          <div className="results-card">
            <h3>סיכום</h3>
            
            {user?.role !== 'sales' && (
              <div className="result-row">
                <span>מחיר עלות:</span>
                <strong>₪{calculateTotalCost().toFixed(2)}</strong>
              </div>
            )}
            
            {isSale && (
              <div className="sale-results">
                <h4>מכירה</h4>
                <div className="result-row highlight">
                  <span>מחיר מכירה:</span>
                  <strong>₪{calculateSellingPrice().toFixed(2)}</strong>
                </div>
                
                {user?.role !== 'sales' && (
                  <>
                    <div className="result-row">
                      <span>רווח:</span>
                      <strong className="profit">₪{calculateProfit().toFixed(2)}</strong>
                    </div>
                    <div className="result-row">
                      <span>אחוז רווח:</span>
                      <strong className="profit">{calculateProfitPercent().toFixed(1)}%</strong>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {isRental && (
              <div className="rental-results">
                <h4>השכרה ({rentalYears} שנים)</h4>
                <div className="result-row highlight">
                  <span>תשלום חודשי:</span>
                  <strong>₪{calculateMonthlyRental().toFixed(2)}</strong>
                </div>
                <div className="result-row">
                  <span>סה״כ לתקופה:</span>
                  <strong>₪{calculateTotalRental().toFixed(2)}</strong>
                </div>
                {user?.role !== 'sales' && (
                  <div className="result-row">
                    <span>סה״כ ריבית:</span>
                    <strong>₪{calculateRentalInterest().toFixed(2)}</strong>
                  </div>
                )}
              </div>
            )}
            
            {!isSale && !isRental && (
              <div className="no-selection">
                בחר מכירה או השכרה
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartCalculator;